import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { FunctionComponent } from 'react';

import { type AffectedProject, isProjectScope } from '../../../../shared/issuesV2';
import { useDialogContext } from '../../../contexts/DialogContext';
import useMultipleModals from '../../../hooks/useMultipleModals';
import * as IssuesV2Store from '../../../redux/issuesV2';
import type { ModalState } from '../../../hooks/useMultipleModals';

const issueDetailsModalTypes = ['ignore', 'unignore', 'export', 'licenseDispute', 'malwareDispute', 'qualityDispute', 'unlink', 'vulnDispute', 'viewPath', 'viewLicense', 'editPackage'] as const;
type IssueDetailsModalType = typeof issueDetailsModalTypes[number];

export type UseDrawerModalsReturn = Record<`${Exclude<IssueDetailsModalType, 'viewPath'>}Modal`, ModalState> & {
  //  The ViewPathModal is slightly different because the `onOpen` function
  //  accepts the locator of the project which we need to use for viewing the path to.
  //  This allows us to use the same viewPathModal for both the top-level ViewPath action button,
  //  as well as each of the ViewPath buttons in the ProjectsTable.
  viewPathModal: Omit<ModalState, 'onOpen'> & {
    onOpen: (affectedProject: AffectedProject) => void;
    //  This is used to tell the ViewPath modal which locator is "active"
    projectLocator: string | null;
  };
  //  Helper for checking if any modal is open.
  isAnyModalOpen: boolean;
};

export const IssueDetailsModalsContext = createContext<UseDrawerModalsReturn>({} as UseDrawerModalsReturn);
/**
 * This hook is a thin wrapper around `useMultipleModals`, which
 * helps manage the state of the multiple modals which appear
 * on the IssueDetails page or above IssueDetailsDrawer.
 *
 * Includes a value which tells us if  _any_ of the Modals are open, as we
 * need to make sure the Drawer doesn't close when they are clicked.
 *
 * This includes reading the DialogContext to check if any system dialog
 * modals are currently active, and including that in the `isAnyModalOpen` check.
 */
export const useIssueDetailsModals = () => useContext<UseDrawerModalsReturn>(IssueDetailsModalsContext);

/**
 * The ViewPath modal is a bit special in that it needs to pull in additional data,
 * as it can be triggered from either the top-level ActionButtons or from the ProjectsTable.
 */
const useViewPathModal = (modalState: ModalState): UseDrawerModalsReturn['viewPathModal'] => {
  //  This stores the locator of the user-project (ie: the affected project).
  //  We need to store it because it is derived from the scope in cases where the user is viewing paths
  //  to the project revision they are currently viewing.
  const [projectLocator, setProjectLocator] = useState<AffectedProject['revisionId'] | null>(null);
  const scope = useSelector(IssuesV2Store.selectors.getScope);

  return {
    isOpen: modalState.isOpen,
    onClose: modalState.onClose,
    projectLocator,
    onOpen: useCallback((affectedProject: AffectedProject) => {
      //  For global + release group scope, we can just use the affected project
      //  revisionId, but if we are in the project scope and the user is about
      //  to click on the project that they are in, we want to make sure to use
      //  whatever revision is in the scope instead (so that it's using whatever revision we're looking at).
      if (isProjectScope(scope) && affectedProject.id === scope.id) {
        setProjectLocator(`${scope.id}$${scope.revision}`);
      } else {
        setProjectLocator(affectedProject.revisionId);
      }

      //  Now we can open the modal.
      modalState.onOpen();
    }, [scope, modalState, setProjectLocator]),
  };
};

/**
 * Provider for the IssueDetailsModalsContext.
 * This allows any child down the component tree to access the
 * state of the modals, and open/close them from anywhere.
 */
const IssueDetailsModalsProvider: FunctionComponent = ({ children }) => {
  const modals = useMultipleModals<IssueDetailsModalType>(...issueDetailsModalTypes);
  const { isAnyDialogOpen } = useDialogContext();
  const viewPathModal = useViewPathModal(modals.viewPathModal);

  //  Check if any system dialog OR any of our modals are open.
  //  Note that this will work without having viewPathModal.isOpen in the deps,
  //  because `viewPathModal.isOpen` uses `modals.viewPathModal.isOpen` anyway.
  const isAnyModalOpen = useMemo(() => {
    return isAnyDialogOpen || (Object.values(modals)).some(({ isOpen }) => isOpen);
  }, [isAnyDialogOpen, modals]);

  //  Store our actual context value.
  const contextValue: UseDrawerModalsReturn = useMemo(() => ({
    ...modals,
    viewPathModal,
    isAnyModalOpen,
  }), [modals, viewPathModal, isAnyModalOpen]);

  return (
    <IssueDetailsModalsContext.Provider value={contextValue}>
      {children}
    </IssueDetailsModalsContext.Provider>
  );
};

export default IssueDetailsModalsProvider;
